// src/pages/users/UsersPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  User,
  Shield,
  Building2,
  Warehouse,
  UserX,
  UserCheck,
  Mail,
  Phone,
  Eye
} from 'lucide-react';
import {
  PageHeader,
  DataTable,
  FilterPanel
} from '../../components/common';
import { getUsers, deleteUser, updateUserStatus } from '../../services/api/users';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getImageUrl, handleImageError } from '../../utils/imageUtils';
import styles from './UsersPage.module.css';

const UsersPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error, confirm } = useAlert();

  // State management
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100]
  });

  // Sorting state
  const [sorting, setSorting] = useState({
    orderBy: 'name',
    orderDirection: 'ASC'
  });

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    roleId: ''
  });

  // Available roles for filtering
  const [availableRoles, setAvailableRoles] = useState([]);

  // Load users when filters or pagination/sorting changes
  useEffect(() => {
    fetchUsers();
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);

      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection
      };

      // Add filters
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.roleId) params.roleId = filters.roleId;

      const response = await getUsers(params);
      setUsers(response.users || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));

      // Extract unique roles for filter dropdown
      const roles = response.users
        .map(user => user.UserRole)
        .filter(role => role)
        .filter((role, index, self) =>
          index === self.findIndex(r => r.id === role.id)
        );
      setAvailableRoles(roles);

    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 403) {
        setLoadingError(t('errors.permissionDenied'));
      } else {
        setLoadingError(t('users.failedToLoadData'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      roleId: ''
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const hasActiveFilters = () => {
    return Object.values(filters).some(value => value !== '');
  };

  const handleUserStatusToggle = async (userId, currentStatus, userName) => {
    const newStatus = !currentStatus;
    const actionText = newStatus ? t('common.active').toLowerCase() : t('common.inactive').toLowerCase();

    const confirmed = await confirm(
      t('users.confirmStatusChange', { action: actionText, userName }),
      {
        title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} ${t('nav.users')}`,
        confirmText: actionText.charAt(0).toUpperCase() + actionText.slice(1),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await updateUserStatus(userId, newStatus);
      setUsers(prev =>
        prev.map(user =>
          user.id === userId
            ? { ...user, isActive: newStatus }
            : user
        )
      );
      success(
        t('users.statusUpdateSuccess', {
          userName,
          status: newStatus ? t('common.active').toLowerCase() : t('common.inactive').toLowerCase()
        }),
        { duration: 3000 }
      );
    } catch (err) {
      console.error('Failed to update user status:', err);
      error(t('users.statusUpdateFailed'));
    }
    setActiveDropdown(null);
  };

  const handleDeleteUser = async (userId, userName) => {
    const confirmed = await confirm(
      t('users.confirmDelete', { userName }),
      {
        title: t('users.deleteUser'),
        confirmText: t('common.delete'),
        cancelText: t('common.cancel')
      }
    );

    if (!confirmed) return;

    try {
      await deleteUser(userId);
      success(t('users.deleteSuccess', { userName }));
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      error(t('users.deleteFailed'));
    }
    setActiveDropdown(null);
  };

  const toggleDropdown = (userId) => {
    setActiveDropdown(activeDropdown === userId ? null : userId);
  };

  // Define filter configuration
  const filterDefinitions = [
    {
      key: 'status',
      label: t('common.status'),
      type: 'select',
      placeholder: t('users.allStatus'),
      options: [
        { value: 'true', label: t('common.active') },
        { value: 'false', label: t('common.inactive') }
      ]
    },
    {
      key: 'roleId',
      label: t('users.role'),
      type: 'select',
      placeholder: t('users.allRoles'),
      options: availableRoles.map(role => ({
        value: role.id.toString(),
        label: role.name
      }))
    }
  ];

  // Define table columns
  const columns = [
    {
      key: 'user',
      header: t('users.user'),
      sortable: true,
      width: '35%',
      render: (row) => (
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>
            {row.profilePicture ? (
              <img
                src={getImageUrl(row.profilePicture)}
                alt={t('users.userAvatarAlt', { name: row.name })}
                className={styles.avatarImage}
                onError={(e) => handleImageError(e)}
              />
            ) : null}
            <div className={`${styles.avatarPlaceholder} ${row.profilePicture ? styles.hidden : ''}`}>
              <User size={20} />
            </div>
          </div>
          <div className={styles.userDetails}>
            <span className={styles.userName}>{row.name}</span>
            <div className={styles.userContact}>
              <Mail size={12} />
              <span>{row.email}</span>
            </div>
            {row.phone && (
              <div className={styles.userContact}>
                <Phone size={12} />
                <span>{row.phone}</span>
              </div>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'role',
      header: t('users.role'),
      width: '18%',
      render: (row) => (
        row.UserRole ? (
          <div className={styles.roleBadge}>
            <Shield size={14} />
            <span>{row.UserRole.name}</span>
          </div>
        ) : (
          <span className={styles.noRole}>{t('users.noRoleAssigned')}</span>
        )
      )
    },
    {
      key: 'assignments',
      header: t('users.assignments'),
      width: '20%',
      render: (row) => (
        <div className={styles.assignmentsList}>
          {row.shops && row.shops.length > 0 && (
            <div className={styles.assignmentItem}>
              <Building2 size={12} />
              <span>{t('users.shopCount', { count: row.shops.length })}</span>
            </div>
          )}
          {row.warehouses && row.warehouses.length > 0 && (
            <div className={styles.assignmentItem}>
              <Warehouse size={12} />
              <span>{t('users.warehouseCount', { count: row.warehouses.length })}</span>
            </div>
          )}
          {(!row.shops || row.shops.length === 0) &&
            (!row.warehouses || row.warehouses.length === 0) && (
              <span className={styles.noAssignments}>{t('users.noAssignments')}</span>
            )}
        </div>
      )
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '12%',
      render: (row) => (
        <div className={`${styles.statusBadge} ${row.isActive ? styles.active : styles.inactive}`}>
          {row.isActive ? (
            <>
              <UserCheck size={14} />
              <span>{t('common.active')}</span>
            </>
          ) : (
            <>
              <UserX size={14} />
              <span>{t('common.inactive')}</span>
            </>
          )}
        </div>
      )
    }
  ];

  // Define table actions
  const tableActions = {
    render: (row) => (
      <div className={styles.actionsWrapper}>
        {hasPermission('view_user') && (
          <Link
            to={`/users/${row.id}`}
            className={`${styles.actionButton} ${styles.viewButton}`}
            title={t('users.viewDetails')}
          >
            <Eye size={14} />
          </Link>
        )}

        {hasPermission('edit_user') && (
          <Link
            to={`/users/${row.id}/edit`}
            className={`${styles.actionButton} ${styles.editButton}`}
            title={t('users.editUser')}
          >
            <Edit size={14} />
          </Link>
        )}

        {(hasPermission('edit_user') || hasPermission('delete_user')) && (
          <div className={styles.dropdownWrapper}>
            <button
              className={`${styles.actionButton} ${styles.moreButton}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleDropdown(row.id);
              }}
              title={t('common.moreActions')}
            >
              <Trash2 size={14} />
            </button>

            {activeDropdown === row.id && (
              <div className={styles.dropdownMenu}>
                {hasPermission('edit_user') && (
                  <button
                    className={styles.dropdownItem}
                    onClick={() => handleUserStatusToggle(row.id, row.isActive, row.name)}
                  >
                    {row.isActive ? <UserX size={14} /> : <UserCheck size={14} />}
                    {row.isActive ? t('users.deactivate') : t('users.activate')}
                  </button>
                )}
                {hasPermission('delete_user') && (
                  <button
                    className={`${styles.dropdownItem} ${styles.deleteItem}`}
                    onClick={() => handleDeleteUser(row.id, row.name)}
                  >
                    <Trash2 size={14} />
                    {t('common.delete')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    ),
    width: '15%',
    frozen: true
  };

  return (
    <div className={styles.usersPage}>
      {/* Page Header */}
      <PageHeader
        title={t('sidebar.allUsers')}
        description={t('users.manageTeamMembers')}
        icon={Users}
        actions={
          hasPermission('create_user') && (
            <Link to="/users/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('sidebar.addUser')}
            </Link>
          )
        }
      />

      {/* Search and Filters Row */}
      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('users.searchUsersPlaceholder')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      {/* Data Table */}
      <DataTable
        data={users}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/users/${row.id}`)}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        error={loadingError}
        emptyMessage={
          hasActiveFilters()
            ? t('users.noUsersFound')
            : t('users.noUsersYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('users.noUsersFoundDesc')
            : t('users.noUsersYetDesc')
        }
        emptyIcon={<Users size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_user') ? (
            <Link to="/users/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('users.addFirstUser')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default UsersPage;